import { Injectable } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { NanorepAccountInfoModel } from 'common/api/models/nanorep-account-info.model';
import { NanorepContext } from './nanorep-context.interface';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { BcConfigService } from 'common/config/provider';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { Settings } from 'common/settings/settings.interface';

@Injectable()
export class NanorepService {
  private accountInfo: NanorepAccountInfoModel;
  private config;

  constructor(private _bcRestApi: BcRestApi,
              LogoutSubscriber: LogoutSubscriber,
              bcConfig: BcConfigService,
              private bcSettingsService: BcSettingsService) {
    LogoutSubscriber.subscribe(() => this._clearAccountInfo());
    this.config = bcConfig.current;
  }

  async getAccountInfo(): Promise<NanorepAccountInfoModel> {
    if (!this.accountInfo) {
      const nanorepAccountInfo = await this._bcRestApi.getNanorepAccountInfo();
      this._validateAccountInfo(nanorepAccountInfo);
      this.accountInfo = nanorepAccountInfo;
    }
    return this.accountInfo;
  }

  sendMessageToProposeAnArticle(chatId, body, lastVisitorMessage): Promise<void> {
    return this._bcRestApi.sendNanorepArticleSuggestion(chatId, body, lastVisitorMessage);
  }

  sendArticleComment(articleId, comment): Promise<void> {
    return this._bcRestApi.sendNanorepArticleComment(articleId, comment);
  }

  async hasNanorepFeature(): Promise<boolean> {
    const settings: Settings = await this.bcSettingsService.getSettings();
    return !!this.config.features.nanorep && this.bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_INTEGRATIONS_NANOREP);
  }

  private _clearAccountInfo() {
    this.accountInfo = null;
  }

  private _validateAccountInfo(accountInfo) {
    if (!accountInfo.nanorepAccountName || !accountInfo.nanorepHost) {
      throw new Error(`Invalid Nanorep account information: accountName: ${accountInfo.nanorepAccountName}, nanorepHost: ${accountInfo.nanorepHost}`);
    }
  }

  buildContext(visitor): NanorepContext {
    const nanorepContext = {
      ...NanorepService._addPropertyIfExists(visitor, 'cityRegion', 'city'),
      ...NanorepService._addPropertyIfExists(visitor, 'country', 'country'),
      ...NanorepService._addPropertyIfExists(visitor, 'website', 'website'),
      ...NanorepService._flattenCustomPreChatFields(visitor.fields.customPreChatFields)
    };
    if (visitor.fields.customFields) {
      nanorepContext.nrContext = NanorepService.buildNanorepContext(visitor);
    } else {
      nanorepContext.nrContext = {};
    }
    return nanorepContext;
  }

  private static buildNanorepContext(visitor): Object {
    if (visitor.fields && visitor.fields.customFields) {
      // TODO: This nrContextMatch section is temporary. This is a quickfix for Arise nrContext issue, it needs to be deleted after the release in October [BLD-1410]
      const nrContextMatch = visitor.fields.customFields.match(/nrContext=({.*?})/);
      if (nrContextMatch && nrContextMatch.length > 1) {
        const formattedCustomFields = nrContextMatch[1].replace(/\\c/g , ',');
        return JSON.parse(formattedCustomFields);
      } else {
        // This section is the final solution for nrContext
        let context = {};
        const items = visitor.fields.customFields.split(',');
        items.forEach((item) => {
          let splittedItem = item.split('=');
          context[splittedItem[0]] = splittedItem[1];
        });
        return context;
      }
    }
    return null;
  }

  private static _addPropertyIfExists(visitor: VisitorInfoModel, propertyName: string, targetPropertyName: string) {
    return visitor.fields[propertyName] ? {
      [targetPropertyName]: visitor.fields[propertyName].value
    } : {};
  }

  private static _flattenCustomPreChatFields(fields) {
    if (!fields) { return; }
    return fields.reduce((previousValue, currentValue) => {
      return {
        ...previousValue,
        [currentValue.label]: currentValue.value
      };
    }, {});
  }
}
